import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DifficultyPerformance } from '../types/testResult';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DifficultyAnalysisChartProps {
  data: DifficultyPerformance[];
  height?: number;
}

/**
 * Difficulty Analysis Chart Component
 */
export const DifficultyAnalysisChart: React.FC<DifficultyAnalysisChartProps> = ({
  data,
  height = 300
}) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll create our own legend
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const difficulty = data[context.dataIndex];
            return [
              `${context.dataset.label}: ${context.parsed.y}%`,
              `Questions: ${difficulty.totalQuestions}`,
              `Correct: ${difficulty.totalCorrect}`,
              `Wrong: ${difficulty.totalWrong}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    },
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'rgba(16, 185, 129, 0.8)'; // Green
      case 'medium':
        return 'rgba(245, 158, 11, 0.8)'; // Yellow
      case 'hard':
        return 'rgba(239, 68, 68, 0.8)'; // Red
      default:
        return 'rgba(107, 114, 128, 0.8)'; // Gray
    }
  };

  const chartData = {
    labels: data.map(item => item.difficulty.charAt(0).toUpperCase() + item.difficulty.slice(1).toLowerCase()),
    datasets: [
      {
        label: 'Performance by Difficulty',
        data: data.map(item => item.averagePercentage),
        backgroundColor: data.map(item => getDifficultyColor(item.difficulty)),
        borderColor: data.map(item => getDifficultyColor(item.difficulty).replace('0.8', '1')),
        borderWidth: 1,
      },
    ],
  };

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500"
        style={{ height: `${height}px` }}
      >
        No difficulty analysis data available
      </div>
    );
  }

  return (
    <div style={{ height: `${height}px` }}>
      <Bar data={chartData} options={chartOptions} />

      {/* Legend with additional info */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
        {data.map((item) => (
          <div key={item.difficulty} className="text-center">
            <div
              className="w-4 h-4 rounded mx-auto mb-1"
              style={{ backgroundColor: getDifficultyColor(item.difficulty) }}
            ></div>
            <div className="font-medium">
              {item.difficulty.charAt(0).toUpperCase() + item.difficulty.slice(1).toLowerCase()}
            </div>
            <div className="text-gray-600">
              {item.totalCorrect}/{item.totalQuestions}
            </div>
            <div className="text-gray-500 text-xs">
              {item.averagePercentage.toFixed(1)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
